import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Chip, Pagination, Spinner, Table } from '@heroui/react';
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import {
  selectVisitsItems,
  selectVisitsIsLoading,
  selectVisitsLimit,
  selectVisitsPage,
  selectVisitsTotal,
  useVisitsStore,
  type Visit,
} from '@/store/visitsSlice';
import { VISIT_STATUS_COLORS, VISIT_STATUS_LABELS } from '@/lib/visitStatus';

const columnHelper = createColumnHelper<Visit>();

const columns = [
  // ФИО — опциональное настраиваемое поле, идентифицирует login
  columnHelper.accessor((row) => row.user.login, {
    id: 'user',
    header: 'Исполнитель',
    cell: (info) => {
      const user = info.row.original.user;
      return (
        <div className="flex flex-col">
          <span className="text-sm font-medium text-foreground">{user.login}</span>
          {user.phone && <span className="text-xs text-muted">{user.phone}</span>}
        </div>
      );
    },
  }),
  columnHelper.accessor((row) => row.user.internalId, {
    id: 'internalId',
    header: 'Внутренний ID',
    cell: (info) => info.getValue() ?? '—',
  }),
  columnHelper.accessor((row) => row.template.title, {
    id: 'template',
    header: 'Шаблон',
    cell: (info) => {
      const template = info.row.original.template;
      return (
        <div className="flex flex-col">
          <span className="text-sm font-medium text-foreground">{template.title}</span>
          <span className="text-xs text-muted">{template.slug}</span>
        </div>
      );
    },
  }),
  columnHelper.accessor('plannedAt', {
    header: 'Запланирован',
    cell: (info) => {
      const v = info.getValue();
      return v ? formatDate(v) : '—';
    },
  }),
  columnHelper.accessor((row) => row.location?.name ?? null, {
    id: 'location',
    header: 'Локация',
    cell: (info) => {
      const location = info.row.original.location;
      if (!location) return '—';
      return (
        <div className="flex flex-col">
          <span className="text-sm text-foreground">{location.name}</span>
          {location.city && <span className="text-xs text-muted">{location.city}</span>}
        </div>
      );
    },
  }),
  columnHelper.accessor('status', {
    header: 'Статус',
    cell: (info) => {
      const status = info.getValue();
      return (
        <Chip color={VISIT_STATUS_COLORS[status]} size="sm" variant="soft">
          {VISIT_STATUS_LABELS[status]}
        </Chip>
      );
    },
  }),
];

function formatDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function VisitsTable() {
  const navigate = useNavigate();
  const items = useVisitsStore(selectVisitsItems);
  const isLoading = useVisitsStore(selectVisitsIsLoading);
  const page = useVisitsStore(selectVisitsPage);
  const limit = useVisitsStore(selectVisitsLimit);
  const total = useVisitsStore(selectVisitsTotal);
  const setPage = useVisitsStore((s) => s.setPage);

  const table = useReactTable({
    columns,
    data: items,
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
    pageCount: -1,
  });

  const totalPages = Math.max(1, Math.ceil(total / limit));
  const pages = useMemo(() => {
    if (totalPages <= 7) return Array.from({ length: totalPages }, (_, i) => i + 1);
    if (page <= 4) return [1, 2, 3, 4, 5, '…', totalPages] as const;
    if (page >= totalPages - 3)
      return [
        1,
        '…',
        totalPages - 4,
        totalPages - 3,
        totalPages - 2,
        totalPages - 1,
        totalPages,
      ] as const;
    return [1, '…', page - 1, page, page + 1, '…', totalPages] as const;
  }, [totalPages, page]);
  const start = total === 0 ? 0 : (page - 1) * limit + 1;
  const end = Math.min(page * limit, total);

  return (
    <Table className="min-h-[280px]">
      <Table.ScrollContainer>
        <Table.Content aria-label="Журнал визитов" className="min-w-[820px]">
          <Table.Header>
            {table.getHeaderGroups()[0]!.headers.map((header) => (
              <Table.Column key={header.id} id={header.id} isRowHeader={header.id === 'user'}>
                {flexRender(header.column.columnDef.header, header.getContext())}
              </Table.Column>
            ))}
          </Table.Header>
          <Table.Body
            renderEmptyState={() =>
              isLoading ? (
                <div className="flex w-full items-center justify-center gap-2 py-10 text-sm text-muted">
                  <Spinner size="sm" />
                  Загрузка…
                </div>
              ) : (
                <div className="flex w-full items-center justify-center py-10 text-sm text-muted">
                  Визиты не найдены
                </div>
              )
            }
          >
            {table.getRowModel().rows.map((row) => (
              <Table.Row
                key={row.id}
                id={row.id}
                className="cursor-pointer"
                onAction={() => navigate(`/visits/${row.original.id}`)}
              >
                {row.getVisibleCells().map((cell) => (
                  <Table.Cell key={cell.id}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </Table.Cell>
                ))}
              </Table.Row>
            ))}
          </Table.Body>
        </Table.Content>
      </Table.ScrollContainer>
      <Table.Footer>
        <Pagination size="sm">
          <Pagination.Summary>
            {start}–{end} из {total}
          </Pagination.Summary>
          <Pagination.Content>
            <Pagination.Item>
              <Pagination.Previous isDisabled={page <= 1} onPress={() => setPage(page - 1)}>
                <Pagination.PreviousIcon />
                Назад
              </Pagination.Previous>
            </Pagination.Item>
            {pages.map((p, idx) =>
              p === '…' ? (
                <Pagination.Item key={`ellipsis-${idx}`}>
                  <span className="px-2 text-sm text-muted">…</span>
                </Pagination.Item>
              ) : (
                <Pagination.Item key={p}>
                  <Pagination.Link isActive={p === page} onPress={() => setPage(p)}>
                    {p}
                  </Pagination.Link>
                </Pagination.Item>
              ),
            )}
            <Pagination.Item>
              <Pagination.Next isDisabled={page >= totalPages} onPress={() => setPage(page + 1)}>
                Вперёд
                <Pagination.NextIcon />
              </Pagination.Next>
            </Pagination.Item>
          </Pagination.Content>
        </Pagination>
      </Table.Footer>
    </Table>
  );
}
